import { format } from 'date-fns';
import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Observable, Observer } from 'rxjs';
import { AlertMessageService } from 'shared/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, ValidationErrors } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { CustomerModel, CustomerNotificationSubscription, Modules, Responsibility, CountryModel, CountryData, NotificationTemplateConfiguration } from 'shared/models';
import { AngularCsv } from 'angular-csv-ext/dist/Angular-csv';
import { AuthService } from 'shared/core';
import { MobileValidationService } from '../../Services/mobile-validation.service';
import * as cronstrue from 'cronstrue';
import { CommonService } from 'shared/core';

@Component({
    selector: 'app-customer-list',
    templateUrl: './customer-list.component.html',
    styleUrls: ['./customer-list.component.css'],
    standalone: false
})
export class CustomerListComponent implements OnInit {
  validateForm!: UntypedFormGroup;
  ApiUrl = environment.Api_Url;
  CustomerList: CustomerModel[] = [];
  CustomerListOriginal: CustomerModel[] = [];
  NewCustomer: CustomerModel = new CustomerModel;
  isVisible = false;
  isLoading: boolean = false;
  isTableLoading: boolean = false;
  typeList: any[] = [];
  PopUpTitle: string = "Add New Customer";
  NameError = 'Enter Customer name';
  exportoptions = {
    headers: ["Customer Name", "Email", "Customer Contact Number", "Address", "State", "Country"]
  };
  fields: any;
  exportfields = [{
    "CustomerName": "",
    "Email": "",
    "CustomerContactNumber": "",
    "Address": "",
    "State": "",
    "Country": ""
  }];
  count: 0;
  permission = {
    View: false,
    Add: false,
    Delete: false
  }

  // Country and mobile validation properties
  countries: CountryModel[] = [];
  selectedCountry: CountryModel;
  mobileValidationError: string = '';
  showMobileError: boolean = false;

  // Notification subscription properties
  availableNotificationTypes: NotificationTemplateConfiguration[] = [];
  customerNotificationTypes: NotificationTemplateConfiguration[] = [];
  showNotificationTypes: boolean = false;
  showCustomSchedule: boolean = false;
  cronScheduleDescription: string = '';
  isLoadingNotificationTypes: boolean = false;

  // Cache for selected notification type IDs to prevent infinite loops
  selectedNotificationTypeIds: number[] = [];
  private isUpdatingNotifications: boolean = false;

  constructor(private fb: UntypedFormBuilder, public http: HttpClient, private alertService: AlertMessageService, private modalService: NzModalService,
    private auth: AuthService, private router: Router, private mobileValidationService: MobileValidationService, private commonService: CommonService) {
    this.countries = CountryData.countries;
    this.selectedCountry = CountryData.getDefaultCountry();
  }

  ngOnInit() {
    this.permission.View = this.auth.CheckResponsibility(Modules.Customer, Responsibility.View);
    this.permission.Add = this.auth.CheckResponsibility(Modules.Customer, Responsibility.Add);
    this.permission.Delete = this.auth.CheckResponsibility(Modules.Customer, Responsibility.Delete);
    if (this.permission.View != true) {
      this.router.navigate(['/home/unauthorized']);
    }
    this.validateForm = this.fb.group({
      CustomerName: [null, [Validators.required, Validators.minLength(3)], [this.NameAsyncValidator]],
      Email: [null, [Validators.pattern("^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}(,[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4})*$")]],
      CustomerContactNumber: [null], // Made optional - validation will be handled on blur
      Address: [null, [Validators.required]],
      //Gst: [null, [Validators.required,  Validators.pattern("^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$")]]
      Gst: [null, [Validators.required]],
      Country: [this.selectedCountry.countryName, [Validators.required]],
      State: [null, [Validators.required]],
      ProformaRequirement: [null]
    });
    this.GetAllCustomer();
    this.loadNotificationTypes();
  }
  get f() { return this.validateForm.controls; }

  GetAllCustomer() {
    this.isTableLoading = true;
    let url = this.ApiUrl + "customer/getallcustomers";
    this.http.get<CustomerModel[]>(url).subscribe(res => {
      this.CustomerList = res;
      this.CustomerListOriginal = res;
      this.exportfields = [];
      this.CustomerListOriginal.forEach(x => {

        this.fields = {};
        this.fields.CustomerName = x.CustomerName;
        this.fields.Email = x.Email;
        this.fields.CustomerContactNumber = x.CustomerContactNumber;
        this.fields.Address = x.Address;
        this.fields.State = x.State;
        this.fields.Country = x.Country;
        this.exportfields.push(this.fields);

      })
      this.isTableLoading = false;
    }, res => {
      this.count++
      if (this.count < 2) {
        this.GetAllCustomer();
      }
    });
  }
  onKeydown(event: any) {
    if (event.target.selectionStart === 0 && event.code === "Space" || event.key === "Enter" && event.keyCode === 13) {

      event.preventDefault();
      event = this.search()
    }
  }

  SaveCustomer() {
    // Validate mobile number before form submission
    this.validateMobileNumberOnBlur();

    // Validate notification preferences
    const notificationValidationError = this.validateNotificationPreferences();

    if (this.validateForm.invalid || this.showMobileError || notificationValidationError) {
      Object.values(this.validateForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsDirty();
          control.updateValueAndValidity({ onlySelf: true });
        }
      });

      if (this.showMobileError) {
        this.alertService.error("Please fix the mobile number validation error before saving.");
      }

      if (notificationValidationError) {
        this.alertService.error(notificationValidationError);
      }

      this.isLoading = false
      return;
    }

    this.NewCustomer.CustomerName = this.f['CustomerName'].value;
    this.NewCustomer.Email = this.f['Email'].value;
    this.NewCustomer.CustomerContactNumber = this.f['CustomerContactNumber'].value;
    this.NewCustomer.Address = this.f['Address'].value;
    this.NewCustomer.Gstnumber = this.f['Gst'].value;
    this.NewCustomer.State = this.f['State'].value;
    this.NewCustomer.Country = this.f['Country'].value;
    this.NewCustomer.ProformaRequirement = this.f['ProformaRequirement'].value ?? null;

    // Prepare notification subscription data
    this.prepareNotificationSubscriptionData();
    // if(!this.Validate(this.NewCustomer))
    // {
    //   this.isLoading=false
    //     return;
    // }
    let url = this.ApiUrl + "customer/addupdatecustomer";
    this.http.post<CustomerModel>(url, this.NewCustomer).subscribe({

      next: res => { this.commonService.clearCache('customer'); this.alertService.success("Customer Saved Successfully"); this.isLoading = this.isVisible = false; this.GetAllCustomer(); },
      error: res => { this.alertService.error("An error has been occured. Please try again"); this.isLoading = this.isVisible = false; },

    });

  }
  Validate(model: CustomerModel) {
    var Isvalid: boolean = true;
    //const emailPattern = "^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}(,[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4})*$";
    if (model.CustomerName == '') {
      this.alertService.error("Enter Customer Name"); Isvalid = false;
    }
    // else if (model.Email == '') {
    //   this.alertService.error("Enter Email"); Isvalid = false;
    // }
    else if (model.Address == '') {
      this.alertService.error("Enter Address"); Isvalid = false;
    }
    else if (model.State == '') {
      this.alertService.error("Enter State"); Isvalid = false;
    }
    else if (model.Country == '') {
      this.alertService.error("Enter State"); Isvalid = false;
    }
    else if (model.Gstnumber == '') {
      this.alertService.error("Enter GST"); Isvalid = false;
    }

    return Isvalid;
  }
  OpenEditPop(data: CustomerModel) {
    this.PopUpTitle = "Modify Customer Details";

    // Set selected country based on customer's country name
    if (data.Country) {
      const country = CountryData.getCountryByName(data.Country);
      if (country) {
        this.selectedCountry = country;
      }
    }

    this.validateForm.setValue(
      {
        CustomerName: data.CustomerName,
        Email: data.Email,
        CustomerContactNumber: data.CustomerContactNumber,
        Address: data.Address,
        Gst: data.Gstnumber,
        State: data.State,
        Country: data.Country,
        ProformaRequirement: data.ProformaRequirement ?? null
      }
    )
    this.NewCustomer = new CustomerModel();
    this.NewCustomer.CustomerId = data.CustomerId;
    this.NewCustomer.IsEmailNotification = data.IsEmailNotification || false;
    this.NewCustomer.IsWhatsappNotification = data.IsWhatsappNotification || false;

    // Initialize notification preferences for existing customer
    this.initializeNotificationPreferences(data);

    // Clear any previous mobile validation errors
    this.showMobileError = false;
    this.mobileValidationError = '';

    this.isVisible = true;
  }

  showModal(): void {
    this.PopUpTitle = "Add New Customer";

    // Reset to default country first
    this.selectedCountry = CountryData.getDefaultCountry();

    // Reset form with default country
    this.validateForm.reset();
    this.validateForm.patchValue({
      Country: this.selectedCountry.countryName
    });

    this.NewCustomer.CustomerName = "";
    this.NewCustomer.CustomerId = 0;
    this.NewCustomer.State = "";
    this.NewCustomer.Country = "";
    this.NewCustomer.Email = "";
    this.NewCustomer.CustomerContactNumber = "";
    this.NewCustomer.Address = "";
    this.NewCustomer.Gstnumber = "";

    // Reset notification preferences
    this.NewCustomer.IsEmailNotification = false;
    this.NewCustomer.IsWhatsappNotification = false;
    this.NewCustomer.NotificationSubscriptions = [];
    this.selectedNotificationTypeIds = [];
    this.showNotificationTypes = false;
    this.showCustomSchedule = false;
    this.isUpdatingNotifications = false;

    // Clear mobile validation errors
    this.showMobileError = false;
    this.mobileValidationError = '';

    this.isVisible = true;
  }

  handleOk(): void {

    this.isLoading = true;
    this.SaveCustomer();

  }

  handleCancel(): void {
    this.isVisible = false;
  }
  export() {

    var exportdate = format(new Date(), "-ddMMyyyy-hhmmss");
    if (this.exportfields.length > 0)
      new AngularCsv(this.exportfields, 'customer-export' + exportdate, this.exportoptions);
  }
  DeleteCustomer() {
    let url = this.ApiUrl + "customer/deletecustomer";
    this.http.post<any>(url, this.NewCustomer).subscribe({

      next: res => {

        this.commonService.clearCache('customer'); this.alertService.success("Customer Deleted Successfully"); this.isLoading = this.isVisible = false;
        this.GetAllCustomer();

      },
      error: res => { this.alertService.error("An error has been occured. Please try again"); this.isLoading = this.isVisible = false; },

    });

  }
  handleDelete(data: CustomerModel) {
    this.NewCustomer = data;
    const modal = this.modalService.confirm({
      nzTitle: 'Confirm',
      nzContent: 'Are you sure to delete Customer Name - ' + data.CustomerName + '?',
      nzOkDanger: true,
      nzOnOk: () => this.DeleteCustomer(),
    });

    setTimeout(() => modal.destroy(), 5000);

  }
  NameAsyncValidator = (control: UntypedFormControl) =>
    new Observable((observer: Observer<ValidationErrors | null>) => {
      setTimeout(() => {
        var res = this.CustomerList.filter(x => x.CustomerId != this.NewCustomer.CustomerId);
        var nre = res.filter(x => x.CustomerName.toLowerCase() == control.value.toLowerCase());
        this.NameError = '';

        if (control.value == '') {
          this.NameError = "Enter Customer name";
          observer.next({ error: true, duplicated: true });
        }
        else if (nre.length > 0) {
          this.NameError = "Customer name already exist";
          // you have to return `{error: true}` to mark it as an error event
          observer.next({ error: true, duplicated: true });
        } else {
          this.NameError = "Enter Customer name";
          observer.next(null);
        }
        observer.complete();
      }, 1);
    });
  searchValue = '';
  visible = false;
  reset(): void {
    this.searchValue = '';
    this.search();
  }
  search(): void {
    this.visible = false;
    const term = (this.searchValue || '').trim().toLowerCase();

    // If search term is empty, reset to original list
    if (!term) {
      this.CustomerList = this.CustomerListOriginal;
      return;
    }

    this.CustomerList = this.CustomerListOriginal.filter((item: CustomerModel) => {
      return (item?.CustomerName || '').toLowerCase().indexOf(term) !== -1
        || (item?.Email || '').toLowerCase().indexOf(term) !== -1
        || (item?.CustomerContactNumber || '').toLowerCase().indexOf(term) !== -1
        || (item?.Address || '').toLowerCase().indexOf(term) !== -1;
    });
  }

  /**
   * Handle country selection change from mobile number country selector
   */
  onCountryChange(country: CountryModel): void {
    this.selectedCountry = country;
    // Update the country dropdown to match
    this.validateForm.patchValue({ Country: country.countryName });
    // Re-validate mobile number if it has a value
    this.validateMobileNumberOnBlur();
  }

  /**
   * Handle country selection change from main country dropdown
   */
  onMainCountryChange(countryName: string): void {
    const country = CountryData.getCountryByName(countryName);
    if (country) {
      this.selectedCountry = country;
      // Re-validate mobile number if it has a value
      this.validateMobileNumberOnBlur();
    }
  }

  /**
   * Get notification status for display
   */
  getNotificationStatus(customer: CustomerModel): { hasEmail: boolean, hasWhatsApp: boolean } {
    return {
      hasEmail: !!(customer.IsEmailNotification && customer.Email),
      hasWhatsApp: !!(customer.IsWhatsappNotification && customer.CustomerContactNumber)
    };
  }

  /**
   * Format mobile number for display
   */
  formatMobileNumberForDisplay(mobileNumber: string, countryCode?: string): string {
    if (!mobileNumber) return '';

    const country = countryCode ? CountryData.getCountryByCode(countryCode) : this.selectedCountry;
    if (country) {
      return this.mobileValidationService.formatMobileNumberForDisplay(mobileNumber, country);
    }
    return mobileNumber;
  }

  /**
   * Validate mobile number on blur event
   */
  validateMobileNumberOnBlur(): void {
    const mobileNumber = this.validateForm.get('CustomerContactNumber')?.value;

    // Clear previous errors
    this.showMobileError = false;
    this.mobileValidationError = '';

    // If field is empty, no validation needed
    if (!mobileNumber || mobileNumber.trim() === '') {
      return;
    }

    // Get country for validation - prioritize form value, fallback to selectedCountry
    let country: CountryModel | undefined;
    const countryName = this.validateForm.get('Country')?.value;

    if (countryName) {
      // Use country from form dropdown
      country = this.mobileValidationService.getCountryByName(countryName);
    } else if (this.selectedCountry) {
      // Fallback to selectedCountry (for mobile number input component)
      country = this.selectedCountry;
    }

    if (!country) {
      this.showMobileError = true;
      this.mobileValidationError = 'Please select a country first';
      return;
    }

    // Validate mobile number
    const validationResult = this.mobileValidationService.validateMobileNumber(mobileNumber, country);
    if (!validationResult.isValid) {
      this.showMobileError = true;
      this.mobileValidationError = validationResult.error || 'Invalid mobile number';
    }
  }

  /**
   * Handle mobile number input blur event
   */
  onMobileNumberBlur(): void {
    this.validateMobileNumberOnBlur();
  }

  /**
   * Handle mobile number input change (clear errors when field becomes empty)
   */
  onMobileNumberChange(value: string): void {
    if (!value || value.trim() === '') {
      this.showMobileError = false;
      this.mobileValidationError = '';
    }
  }

  /**
   * Validate notification preferences before form submission
   * Returns error message if validation fails, null if valid
   */
  private validateNotificationPreferences(): string | null {
    // Check WhatsApp notification preference
    if (this.NewCustomer.IsWhatsappNotification) {
      if (!this.validateMobileNumberForNotification()) {
        return "WhatsApp notifications are enabled but no valid mobile number is provided. Please enter a valid mobile number or disable WhatsApp notifications.";
      }
    }

    // Check Email notification preference
    if (this.NewCustomer.IsEmailNotification) {
      if (!this.validateEmailForNotification()) {
        return "Email notifications are enabled but no valid email address is provided. Please enter a valid email address or disable email notifications.";
      }
    }

    return null; // All validations passed
  }

  /**
   * Validate mobile number for WhatsApp notifications
   * Returns true if valid, false if invalid
   */
  private validateMobileNumberForNotification(): boolean {
    const mobileNumber = this.validateForm.get('CustomerContactNumber')?.value;

    // Check if mobile number is empty
    if (!mobileNumber || mobileNumber.trim() === '') {
      return false;
    }

    // Get country for validation
    let country: CountryModel | undefined;
    const countryName = this.validateForm.get('Country')?.value;

    if (countryName) {
      country = this.mobileValidationService.getCountryByName(countryName);
    } else if (this.selectedCountry) {
      country = this.selectedCountry;
    }

    if (!country) {
      return false;
    }

    // Validate mobile number (single number only, no comma-separated values)
    const validationResult = this.mobileValidationService.validateMobileNumber(mobileNumber, country);
    return validationResult.isValid;
  }

  /**
   * Validate email address for email notifications
   * Returns true if valid, false if invalid
   * Supports multiple comma-separated email addresses
   */
  private validateEmailForNotification(): boolean {
    const email = this.validateForm.get('Email')?.value;

    // Check if email is empty
    if (!email || email.trim() === '') {
      return false;
    }

    // Use the same email pattern validation as the form (supports multiple emails)
    const emailPattern = /^[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4}(,[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,4})*$/;
    return emailPattern.test(email.toLowerCase());
  }

  // ===== NOTIFICATION TYPE SUBSCRIPTION METHODS =====

  /**
   * Load available notification types for customers
   */
  loadNotificationTypes(): void {
    this.isLoadingNotificationTypes = true;
    const url = this.ApiUrl + "notificationconfig/getnotificationtemplateconfigurations";

    this.http.get<NotificationTemplateConfiguration[]>(url).subscribe({
      next: (res) => {
        // Filter only customer-allowed notification types
        this.availableNotificationTypes = res.filter(config =>
          config.CustomerAllowed && !config.Disabled
        ).sort((a, b) => (a.SortOrder || 0) - (b.SortOrder || 0));

        this.isLoadingNotificationTypes = false;
      },
      error: (error) => {
        console.error('Error loading notification types:', error);
        this.isLoadingNotificationTypes = false;
        this.alertService.error('Failed to load notification types');
      }
    });
  }

  /**
   * Handle WhatsApp notification toggle change
   */
  onWhatsAppNotificationChange(enabled: boolean): void {
    // Prevent infinite loops
    if (this.isUpdatingNotifications) {
      return;
    }

    this.isUpdatingNotifications = true;

    try {
      this.NewCustomer.IsWhatsappNotification = enabled;
      this.showNotificationTypes = enabled;

      if (!enabled) {
        // Clear notification subscriptions when WhatsApp is disabled
        this.NewCustomer.NotificationSubscriptions = [];
        this.selectedNotificationTypeIds = [];
        this.showCustomSchedule = false;
      } else {
        // Update selected IDs when enabling WhatsApp
        this.updateSelectedNotificationTypeIds();
      }
    } finally {
      this.isUpdatingNotifications = false;
    }
  }

  /**
   * Handle notification type selection change
   */
  onNotificationTypeSelectionChange(selectedIds: number[]): void {
    // Prevent infinite loops
    if (this.isUpdatingNotifications) {
      return;
    }

    this.isUpdatingNotifications = true;

    try {
      // Update the cached selected IDs first
      this.selectedNotificationTypeIds = [...selectedIds];

      // Update notification subscriptions based on selected types
      this.updateNotificationSubscriptions(selectedIds);
    } finally {
      this.isUpdatingNotifications = false;
    }
  }

  /**
   * Update notification subscriptions based on selected notification types
   */
  private updateNotificationSubscriptions(selectedIds: number[]): void {
    const newSubscriptions: CustomerNotificationSubscription[] = [];

    selectedIds.forEach(configId => {
      const config = this.availableNotificationTypes.find(c => c.ConfigurationId === configId);
      if (config) {
        const existingSubscription = this.NewCustomer.NotificationSubscriptions.find(s => s.ConfigurationId === configId);

        newSubscriptions.push({
          NotificationGroupUserId: existingSubscription?.NotificationGroupUserId || 0,
          ConfigurationId: configId,
          NotificationTypeName: config.DisplayName || '',
          IsScheduleRequired: config.SchedulingRequired || false,
          UseDefaultSchedule: existingSubscription?.UseDefaultSchedule ?? true,
          CronScheduleExpression: existingSubscription?.CronScheduleExpression || '0 10 * * *',
          ScheduleDescription: existingSubscription?.ScheduleDescription || 'Daily at 10:00 AM IST',
          PreferredNotificationTime: existingSubscription?.PreferredNotificationTime || '10:00:00'
        });
      }
    });

    this.NewCustomer.NotificationSubscriptions = newSubscriptions;

    // Check if any selected notification requires custom scheduling
    this.updateScheduleVisibility();
  }

  /**
   * Update schedule visibility based on selected notification types
   */
  private updateScheduleVisibility(): void {
    const hasScheduleRequired = this.NewCustomer.NotificationSubscriptions.some(s => s.IsScheduleRequired);
    this.showCustomSchedule = hasScheduleRequired && this.NewCustomer.NotificationSubscriptions.some(s => !s.UseDefaultSchedule);
  }

  /**
   * Handle custom schedule toggle for a specific notification type
   */
  onCustomScheduleToggle(configId: number, useCustom: boolean): void {
    // Prevent infinite loops during updates
    if (this.isUpdatingNotifications) {
      return;
    }

    const subscription = this.NewCustomer.NotificationSubscriptions.find(s => s.ConfigurationId === configId);
    if (subscription) {
      subscription.UseDefaultSchedule = !useCustom;
      if (!useCustom) {
        // Reset to default when switching back
        subscription.CronScheduleExpression = '0 10 * * *';
        subscription.ScheduleDescription = 'Daily at 10:00 AM IST';
      }
    }
    this.updateScheduleVisibility();
  }

  /**
   * Handle cron expression change for a specific notification type
   */
  onCronExpressionChange(configId: number, cronExpression: string): void {
    // Prevent infinite loops during updates
    if (this.isUpdatingNotifications) {
      return;
    }

    const subscription = this.NewCustomer.NotificationSubscriptions.find(s => s.ConfigurationId === configId);
    if (subscription) {
      subscription.CronScheduleExpression = cronExpression;
      subscription.ScheduleDescription = this.getCronDescription(cronExpression);
    }
  }

  /**
   * Get human-readable description of cron expression
   */
  getCronDescription(cronExpression: string): string {
    try {
      if (cronExpression && cronExpression.trim()) {
        return cronstrue.toString(cronExpression);
      }
    } catch (error) {
      // Don't log warnings for empty expressions
      if (cronExpression && cronExpression.trim()) {
        console.warn('Invalid cron expression:', cronExpression);
      }
    }
    return cronExpression && cronExpression.trim() ? 'Invalid schedule expression' : 'Daily at 10:00 AM IST';
  }

  /**
   * Validate cron expression
   */
  validateCronExpression(cronExpression: string): boolean {
    try {
      if (!cronExpression || !cronExpression.trim()) {
        return true; // Empty expressions are considered valid (will use default)
      }
      cronstrue.toString(cronExpression);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Initialize notification preferences for existing customer
   * Loads existing notification subscriptions from the backend
   */
  private initializeNotificationPreferences(customer: CustomerModel): void {
    // Prevent infinite loops during initialization
    this.isUpdatingNotifications = true;

    try {
      // Load notification subscriptions from customer data
      this.NewCustomer.NotificationSubscriptions = customer.NotificationSubscriptions || [];

      // Update cached selected IDs
      this.updateSelectedNotificationTypeIds();

      // Set visibility flags
      this.showNotificationTypes = this.NewCustomer.IsWhatsappNotification;
      this.showCustomSchedule = false;

      // Update schedule visibility based on loaded subscriptions
      if (this.NewCustomer.IsWhatsappNotification && this.NewCustomer.NotificationSubscriptions.length > 0) {
        this.updateScheduleVisibility();
      }
    } finally {
      this.isUpdatingNotifications = false;
    }
  }

  /**
   * Update the cached selected notification type IDs
   */
  private updateSelectedNotificationTypeIds(): void {
    this.selectedNotificationTypeIds = this.NewCustomer.NotificationSubscriptions.map(sub => sub.ConfigurationId);
  }

  /**
   * Get selected notification type IDs for the dropdown (deprecated - use selectedNotificationTypeIds property)
   */
  getSelectedNotificationTypeIds(): number[] {
    return this.selectedNotificationTypeIds;
  }

  /**
   * Load customer notification subscriptions from backend
   * This method would be called to fetch existing subscriptions for editing
   */
  private loadCustomerNotificationSubscriptions(customerId: number): void {
    // TODO: Implement API call to load customer notification subscriptions
    // const url = this.ApiUrl + `customer/getnotificationsubscriptions/${customerId}`;
    // this.http.get<CustomerNotificationSubscription[]>(url).subscribe({
    //   next: (subscriptions) => {
    //     this.populateNotificationPreferences(subscriptions);
    //   },
    //   error: (error) => {
    //     console.error('Error loading customer notification subscriptions:', error);
    //   }
    // });
  }

  /**
   * Prepare notification subscription data before saving
   * This ensures the data is in the correct format for the backend
   */
  private prepareNotificationSubscriptionData(): void {
    // Ensure notification subscriptions are properly formatted
    if (this.NewCustomer.NotificationSubscriptions) {
      this.NewCustomer.NotificationSubscriptions.forEach(subscription => {
        // Validate cron expressions
        if (subscription.CronScheduleExpression && !this.validateCronExpression(subscription.CronScheduleExpression)) {
          // Reset to default if invalid
          subscription.CronScheduleExpression = '0 10 * * *';
          subscription.ScheduleDescription = 'Daily at 10:00 AM IST';
          subscription.UseDefaultSchedule = true;
        }
      });
    }

    // If WhatsApp is disabled, clear notification subscriptions
    if (!this.NewCustomer.IsWhatsappNotification) {
      this.NewCustomer.NotificationSubscriptions = [];
    }

    // Log the data being sent (for debugging)
    console.log('Customer notification data being saved:', {
      customerId: this.NewCustomer.CustomerId,
      isEmailNotification: this.NewCustomer.IsEmailNotification,
      isWhatsappNotification: this.NewCustomer.IsWhatsappNotification,
      notificationSubscriptions: this.NewCustomer.NotificationSubscriptions
    });
  }

}
